import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useLocation } from 'react-router-dom';
import {
  layoutDirState,
  layoutTypeState,
  menuTextTypeState,
  menuTypeState,
} from '@atom/setting/homepageSet';
import { IBoardSet } from '@typings/menu/boardSetType';
import { LAYOUT_TYPE, LAYOUT_TYPE_VALUE } from '@typings/setting/layoutType';
import { LAYOUT_DIR } from '@typings/setting/layoutDir';
import { TOOLTIP_DIR_TYPE } from '@typings/common/tooltipDirType';
import { BOARD_TYPE } from '@typings/boardType';
import {
  MENU_TEXT_TYPE,
  MENU_TEXT_TYPE_VALUE,
} from '@typings/menu/menuTextType';
import {
  SUB_MENU_DIRECTION,
  SUB_MENU_DIRECTION_VALUE,
  SUB_MENU_FLEX_DIRECTION,
  SUB_MENU_FLEX_DIRECTION_VALUE,
} from '@typings/menu/subMenuDirectionType';
import NavItem from './NavItem';
import NavFolder from './NavFolder';
import { homeSettingState } from '@atom/setting/homeSettingState';
import {
  chatDetailIdState,
  showNavState,
  subMenuMobileHeightState,
} from '@atom/setting/navigation';
import {
  MENU_ITEM_TYPE,
  MENU_ITEM_TYPE_VALUE,
} from '@typings/menu/menuItemType';
import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import { SettingCollectionPath } from '@typings/collections.enum';
import { IsubMenuSetting } from '@typings/setting/subMenuSetting.interface';
import { CollectionPath } from '@typings/collections.enum';
import useWindowWidth from '@hooks/useWindowWidth';

// 메뉴 텍스트 정렬 매핑
const menuAlignMap = {
  [MENU_TEXT_TYPE.LEFT]: 'flex-start',
  [MENU_TEXT_TYPE.CENTER]: 'center',
  [MENU_TEXT_TYPE.RIGHT]: 'flex-end',
};

// 서브메뉴 방향별 위치 스타일 매핑
const subMenuDirectionMap = {
  [SUB_MENU_DIRECTION.TOPLEFT]: css`
    top: 40px;
    left: 24px;
  `,
  [SUB_MENU_DIRECTION.TOPCENTER]: css`
    top: 40px;
    left: 50%;
    transform: translateX(-50%);
  `,
  [SUB_MENU_DIRECTION.TOPRIGHT]: css`
    top: 40px;
    right: 24px;
  `,
  [SUB_MENU_DIRECTION.BOTTOMLEFT]: css`
    bottom: 40px;
    left: 24px;
  `,
  [SUB_MENU_DIRECTION.BOTTOMCENTER]: css`
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
  `,
  [SUB_MENU_DIRECTION.BOTTOMRIGHT]: css`
    bottom: 40px;
    right: 24px;
  `,
  [SUB_MENU_DIRECTION.MIDDLELEFT]: css`
    top: 50%;
    left: 24px;
    transform: translateY(-50%);
  `,
  [SUB_MENU_DIRECTION.MIDDLERIGHT]: css`
    top: 50%;
    right: 24px;
    transform: translateY(-50%);
  `,
};


// 레이아웃 타입별 정렬 스타일 매핑
const getAlignmentStyle = (
  align: MENU_TEXT_TYPE_VALUE,
  layoutType: LAYOUT_TYPE_VALUE,
) => {
  if (layoutType === LAYOUT_TYPE.TOP) {
    return `justify-content: ${menuAlignMap[align]};`;
  } else {
    return `align-items: ${menuAlignMap[align]};`;
  }
};

// 서브 메뉴 래퍼
const SubMenuWrapper = styled(motion.div) <{
  $align: MENU_TEXT_TYPE_VALUE;
  $layoutType: LAYOUT_TYPE_VALUE;
  $direction?: SUB_MENU_DIRECTION_VALUE;
  $flexDirection?: SUB_MENU_FLEX_DIRECTION_VALUE;
  $menuItemType?: string;
  isVisible?: boolean;
  $mobileVisible?: boolean;
}>`
  display: flex;
  position: relative;
  width: max-content;
  /* 기본은 세로(column) */
  flex-direction: column;
  flex-wrap: wrap;
  gap: 8px;
  z-index: 2;
  padding: 12px;
  position: fixed;


  ${({ $direction }) => $direction && subMenuDirectionMap[$direction]}

  // 정렬 스타일 적용
  ${({ $align, $layoutType }) => getAlignmentStyle($align, $layoutType)}
  
  /* 📍 메뉴 타입별 추가 스타일 */
  ${({ $menuItemType, $layoutType }) => {
    if ($layoutType === LAYOUT_TYPE.GAMEUI) {
      return $menuItemType === 'image'
        ? `
        /* IMAGE 타입 서브메뉴 스타일 */
        padding: 8px;
        
        /* 이미지 메뉴들이 더 촘촘하게 배치 */
        .nav-item {
          min-width: 60px;
          max-width: 80px;
        }
      `
        : `
        /* TEXT 타입 서브메뉴 스타일 */
        padding: 12px;
        
        /* 텍스트 메뉴들이 더 여유있게 배치 */
        .nav-item {
          min-width: 80px;
          padding: 8px 12px;
        }
      `;
    }
    return '';
  }}

  a {
    width: auto;
  }

    /* 서브메뉴 슬라이드 애니메이션 (아래 → 위 등장, 위 → 아래 퇴장) */
  @keyframes submenu-slide-in {
    0% { transform: translateY(140%) translateZ(0); opacity: 0; }
    100% { transform: translateY(0) translateZ(0); opacity: 1; }
  }
  @keyframes submenu-slide-out {
    0% { transform: translateY(0) translateZ(0); opacity: 1; }
    100% { transform: translateY(140%) translateZ(0); opacity: 0; }
  }
  /* 서브메뉴 슬라이드 애니메이션 (위 → 아래 등장, 아래 → 위 퇴장) - TOP 위치용 */
  @keyframes submenu-slide-in-top {
    0% { transform: translateY(-140%) translateZ(0); opacity: 0; }
    100% { transform: translateY(0) translateZ(0); opacity: 1; }
  }
  @keyframes submenu-slide-out-top {
    0% { transform: translateY(0) translateZ(0); opacity: 1; }
    100% { transform: translateY(-140%) translateZ(0); opacity: 0; }
  }
  /* 위쪽 중앙 - X축 중앙 유지 상태에서 Y 방향 슬라이드 */
  @keyframes submenu-slide-in-top-center {
    0% { transform: translateX(-50%) translateY(-140%) translateZ(0); opacity: 0; }
    100% { transform: translateX(-50%) translateY(0) translateZ(0); opacity: 1; }
  }
  @keyframes submenu-slide-out-top-center {
    0% { transform: translateX(-50%) translateY(0) translateZ(0); opacity: 1; }
    100% { transform: translateX(-50%) translateY(-140%) translateZ(0); opacity: 0; }
  }
  /* 아래쪽 중앙 - X축 중앙 유지 상태에서 Y 방향 슬라이드 */
  @keyframes submenu-slide-in-bottom-center {
    0% { transform: translateX(-50%) translateY(140%) translateZ(0); opacity: 0; }
    100% { transform: translateX(-50%) translateY(0) translateZ(0); opacity: 1; }
  }
  @keyframes submenu-slide-out-bottom-center {
    0% { transform: translateX(-50%) translateY(0) translateZ(0); opacity: 1; }
    100% { transform: translateX(-50%) translateY(140%) translateZ(0); opacity: 0; }
  }
  /* 서브메뉴 슬라이드 애니메이션 - MIDDLE 위치용 (좌/우 기준, Y는 -50% 유지) */
  @keyframes submenu-slide-in-middle-left {
    0% { transform: translateX(-140%) translateY(-50%) translateZ(0); opacity: 0; }
    100% { transform: translateX(0) translateY(-50%) translateZ(0); opacity: 1; }
  }
  @keyframes submenu-slide-out-middle-left {
    0% { transform: translateX(0) translateY(-50%) translateZ(0); opacity: 1; }
    100% { transform: translateX(-140%) translateY(-50%) translateZ(0); opacity: 0; }
  }
  @keyframes submenu-slide-in-middle-right {
    0% { transform: translateX(140%) translateY(-50%) translateZ(0); opacity: 0; }
    100% { transform: translateX(0) translateY(-50%) translateZ(0); opacity: 1; }
  }
  @keyframes submenu-slide-out-middle-right {
    0% { transform: translateX(0) translateY(-50%) translateZ(0); opacity: 1; }
    100% { transform: translateX(140%) translateY(-50%) translateZ(0); opacity: 0; }
  }

  animation: ${({ isVisible, $direction }) => {
    const isTop = $direction === SUB_MENU_DIRECTION.TOPLEFT || $direction === SUB_MENU_DIRECTION.TOPRIGHT;
    const isTopCenter = $direction === SUB_MENU_DIRECTION.TOPCENTER;
    const isBottomCenter = $direction === SUB_MENU_DIRECTION.BOTTOMCENTER;
    const isMiddleLeft = $direction === SUB_MENU_DIRECTION.MIDDLELEFT;
    const isMiddleRight = $direction === SUB_MENU_DIRECTION.MIDDLERIGHT;
    const inAni = isTopCenter
      ? 'submenu-slide-in-top-center'
      : isBottomCenter
        ? 'submenu-slide-in-bottom-center'
        : isMiddleLeft
          ? 'submenu-slide-in-middle-left'
          : isMiddleRight
            ? 'submenu-slide-in-middle-right'
            : (isTop ? 'submenu-slide-in-top' : 'submenu-slide-in');
    const outAni = isTopCenter
      ? 'submenu-slide-out-top-center'
      : isBottomCenter
        ? 'submenu-slide-out-bottom-center'
        : isMiddleLeft
          ? 'submenu-slide-out-middle-left'
          : isMiddleRight
            ? 'submenu-slide-out-middle-right'
            : (isTop ? 'submenu-slide-out-top' : 'submenu-slide-out');
    return isVisible
      ? `${inAni} 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards`
      : `${outAni} 0.35s cubic-bezier(0.4, 0, 0.2, 1) forwards`;
  }};
  will-change: transform, opacity;
  pointer-events: ${(props) => (props.isVisible ? 'auto' : 'none')};
  
  /* $flexDirection이 주어지면 항상 최종적으로 오버라이드 (모바일 미디어쿼리 제외) */
  ${({ $flexDirection }) =>
    $flexDirection && css`
      flex-direction: ${$flexDirection};
    `}
  
  // 모바일 반응형: 화면 하단 도킹 + 스크롤 방향에 따라 슬라이드 표시/숨김
  @media (max-width: 480px) {
    position: fixed;
    left: 0 !important;
    right: 0 !important;
    top: auto !important;
    bottom: 0 !important;
    width: 100dvw;
    max-width: 100dvw;
    gap: 12px;
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
    flex-direction: row;
    flex-wrap: nowrap; /* 1열 고정 */
    justify-content: flex-start; /* 좌측부터 나열 */
    align-items: center;
    background: linear-gradient(to top, var(--menuBackgroundColor) 50%, rgba(0,0,0,0) 100%);
    animation: none !important; /* 데스크톱용 키프레임 비활성화 */
    transform: translateY(${({ $mobileVisible }) => ($mobileVisible ? '0' : '110%')});
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: ${({ $mobileVisible }) => ($mobileVisible ? 'auto' : 'none')};
    z-index: 3;

    /* 가로 스크롤 허용 (1열 유지) */
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none; /* Firefox */
  }

  /* 모바일 스크롤바 숨김 */
  @media (max-width: 480px) {
    &::-webkit-scrollbar { display: none; height: 0; }
    a, .nav-item, button {
      flex: 0 0 auto; /* 줄바꿈 방지, 가로로 나열 */
      white-space: nowrap; /* 텍스트 줄바꿈 방지 */
    }
  }
`;

interface SubMenuListProps {
  tooltipDir?: any;
  isVisible?: boolean;
}

function SubMenuList({ tooltipDir, isVisible }: SubMenuListProps) {
  const location = useLocation();
  const ww = useWindowWidth();
  const isMobile = (ww || 0) <= 480;
  const chatDetailId = useRecoilValue(chatDetailIdState);
  const layoutType = useRecoilValue(layoutTypeState);
  const menuType = useRecoilValue(menuTypeState);
  const layoutDir = useRecoilValue(layoutDirState);
  const homeSet = useRecoilValue(homeSettingState);
  const showNav = useRecoilValue(showNavState);
  // 보드셋 전체를 읽어오고, SUBMENU 설정은 순서/id만 사용
  const { data: boardSetList } = useCollection<IBoardSet>(CollectionPath.BOARD_SET, {
    listen: true,
  });

  // 서브메뉴 전용 설정 문서
  const { data: subMenuSet } = useDocument<IsubMenuSetting>(
    SettingCollectionPath.SUBMENU,
    { listen: true },
  );

  const [menuTextType, setMenuTextTypeAtom] =
    useRecoilState<MENU_TEXT_TYPE_VALUE>(menuTextTypeState);

  // SUBMENU 설정 문서 기준으로 동작
  const currentSubMenuDirection: SUB_MENU_DIRECTION_VALUE =
    subMenuSet?.subMenuDirection || SUB_MENU_DIRECTION.TOPRIGHT;

  const currentSubMenuFlexDirection: SUB_MENU_FLEX_DIRECTION_VALUE =
    subMenuSet?.subMenuFlexDirection || SUB_MENU_FLEX_DIRECTION.ROW;

  const currentSubMenuItemType: MENU_ITEM_TYPE_VALUE =
    subMenuSet?.subMenuItemType || MENU_ITEM_TYPE.TEXT;

  // 활성화 여부 (기본 true). 훅 호출 순서 유지를 위해 조기 반환은 하지 않음
  const isSubMenuActive = (subMenuSet as any)?.isActive !== false;

  // 모바일: 스크롤 방향에 따라 서브메뉴 보이기/숨기기
  const [mobileVisible, setMobileVisible] = useState(true);
  const lastYRef = useRef<number>(typeof window !== 'undefined' ? window.scrollY : 0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const setSubMenuMobileHeight = useSetRecoilState(subMenuMobileHeightState);

  useEffect(() => {
    const onScroll = () => {
      if (!isMobile) return;
      const currentY = window.scrollY || 0;
      const lastY = lastYRef.current || 0;
      const delta = currentY - lastY;
      const threshold = 8;
      if (delta > threshold) {
        setMobileVisible(false);
      } else if (delta < -threshold) {
        setMobileVisible(true);
      }
      lastYRef.current = currentY;
    };
    window.addEventListener('scroll', onScroll, { passive: true } as any);
    return () => window.removeEventListener('scroll', onScroll as any);
  }, [isMobile]);

  // SubMenuList 높이를 측정해 atom에 반영 (모바일에서 콘텐츠 paddingBottom에 사용)
  useEffect(() => {
    if (!isMobile) {
      setSubMenuMobileHeight(0);
      return;
    }
    const el = wrapperRef.current;
    if (!el) return;

    const update = () => {
      const h = el.getBoundingClientRect().height;
      setSubMenuMobileHeight(mobileVisible ? h : 0);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMobile, mobileVisible, setSubMenuMobileHeight]);

  // 디엠 채팅방 상세 뷰에서는 SubMenuList 숨김
  const isDmChatView =
    location.pathname.startsWith('/chat') && !!chatDetailId;
  // 모바일의 /chat 페이지에서는 SubMenuList를 항상 숨김
  const isMobileChatView = isMobile && location.pathname.startsWith('/chat');
  const shouldHideSubMenu = isDmChatView || isMobileChatView;

  useEffect(() => {
    if (shouldHideSubMenu) {
      setSubMenuMobileHeight(0);
    }
  }, [shouldHideSubMenu, setSubMenuMobileHeight]);


  // subMenus: isSubMenu && isSubShow 조건을 만족하는 항목만, 저장 순서를 우선 적용해 구성
  const subMenus = useMemo(() => {
    if (!isSubMenuActive) return [];
    const all = (boardSetList || []) as any[];
    const ids: string[] = ((subMenuSet as any)?.subMenuOrder || []) as any[];

    // 후보: isSubMenu === true && isSubShow !== false
    const candidates = all.filter(
      (i: any) => i?.isSubMenu === true && i?.isSubShow !== false,
    );

    if (Array.isArray(ids) && ids.length > 0) {
      const idSet = new Set(ids.map((v) => String(v)));
      const byId = new Map(candidates.map((i) => [String(i.id || ''), i]));
      const ordered = ids
        .map((id) => byId.get(String(id)))
        .filter(Boolean) as any[];

      // 나머지: 저장된 순서에 없는 후보들(원본 후보 순서 유지)
      const remaining = candidates.filter(
        (i) => !idSet.has(String(i.id || '')),
      );
      return [...ordered, ...remaining];
    }
    // subMenuOrder가 비어 있으면 원본 리스트(candidates) 순서 유지
    return candidates;
  }, [subMenuSet, boardSetList, isSubMenuActive]);

  useEffect(() => {
    if (homeSet && homeSet.menu)
      setMenuTextTypeAtom(homeSet.menu?.menuTextType || MENU_TEXT_TYPE.LEFT);
  }, [homeSet]);


  // subImgURL 우선순위 적용 헬퍼
  // - subImgURL이 있으면 imgURL을 subImgURL로, hoverImgURL을 subHoverImgURL로 대체
  // - subImgURL이 없으면 원본 imgURL / hoverImgURL 유지
  const applySubImages = (item: any) => ({
    ...item,
    imgURL: item.subImgURL ? item.subImgURL : item.imgURL,
    hoverImgURL: item.subImgURL
      ? (item.subHoverImgURL || '')
      : item.hoverImgURL,
  });

  // 메뉴 아이템 렌더링 함수
  const renderMenuItem = (list: any, index: number) => {
    if (list.type === BOARD_TYPE.FOLDER) {
      // 폴더의 자식은 같은 설정 리스트에서 folderID로 필터링
      const filteredList = subMenus.filter((item: any) => item.folderID === list.id);

      return (
        <NavFolder
          key={list.id}
          data={applySubImages(list)}
          subMenuList={filteredList.map(applySubImages)}
          menuItemType={currentSubMenuItemType}
          menuTextType={menuTextType}
          layoutType={LAYOUT_TYPE.TOP} // 드롭다운을 위해 언제나 top설정
          tooltipDir={
            tooltipDir || layoutDir === LAYOUT_DIR.LEFT
              ? TOOLTIP_DIR_TYPE.RIGHT
              : TOOLTIP_DIR_TYPE.LEFT
          }
          noChangeMobile={true}
        />
      );
    }

    return (
      <NavItem
        key={list.id}
        list={applySubImages(list)}
        tooltipDir={
          (tooltipDir || layoutDir === LAYOUT_DIR.LEFT
            ? TOOLTIP_DIR_TYPE.RIGHT
            : TOOLTIP_DIR_TYPE.LEFT)
        }
        layoutType={LAYOUT_TYPE.TOP}
        menuTextType={menuTextType}
        menuItemType={currentSubMenuItemType}
        noChangeMobile={true}
      />
    );
  };

  // 서브 메뉴 리스트 생성
  const subMenuList = subMenus.map((list, index) =>
    renderMenuItem(list, index),
  );

  // 서브 메뉴가 없으면 null 반환
  if (subMenuList.length === 0 || shouldHideSubMenu) {
    return null;
  }

  const baseVisible = (isVisible === undefined ? true : isVisible) && !!showNav && isSubMenuActive;
  const isVisibleFinal = baseVisible && (!isMobile || mobileVisible);



  return (
    <SubMenuWrapper
      ref={wrapperRef}
      $align={menuTextType}
      $layoutType={layoutType}
      $direction={currentSubMenuDirection}
      $flexDirection={currentSubMenuFlexDirection}
      $menuItemType={currentSubMenuItemType}
      isVisible={isVisibleFinal}
      $mobileVisible={isVisibleFinal}
    >
      {subMenuList}
    </SubMenuWrapper>
  );
}

export default SubMenuList;
